import React from 'react';
import {
    Box,
    Button,
    Flex,
    HStack,
    Text,
} from '@chakra-ui/react';
import SearchableSelect from './SearchableSelect';

export const PAGE_SIZE_OPTIONS = [5, 15, 30, 50];

const PAGE_SIZE_SELECT_OPTIONS = PAGE_SIZE_OPTIONS.map(value => ({
    value,
    label: String(value),
}));

const buildPageItems = (totalPages, currentPage) => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const relevantPages = [...new Set([
        1,
        currentPage - 1,
        currentPage,
        currentPage + 1,
        totalPages,
    ])]
        .filter(page => page >= 1 && page <= totalPages)
        .sort((a, b) => a - b);

    return relevantPages.reduce((items, page, index) => {
        const previousPage = relevantPages[index - 1];
        if (previousPage) {
            const gap = page - previousPage;
            if (gap === 2) items.push(previousPage + 1);
            if (gap > 2) items.push(`ellipsis-${previousPage}-${page}`);
        }
        items.push(page);
        return items;
    }, []);
};

const TablePagination = ({
    page,
    pageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    pageSizeOptions = PAGE_SIZE_SELECT_OPTIONS,
}) => {
    if (totalItems <= 0) return null;

    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const firstItem = ((safePage - 1) * pageSize) + 1;
    const lastItem = Math.min(safePage * pageSize, totalItems);
    const pageItems = buildPageItems(totalPages, safePage);

    return (
        <Flex
            align={{ base: 'stretch', md: 'center' }}
            justify="space-between"
            direction={{ base: 'column', md: 'row' }}
            gap={3}
            px={4}
            py={3}
            borderTop="1px solid"
            borderColor="borderPrimary"
            bg="surfaceRaised"
        >
            <Flex align="center" gap={3} flexWrap="wrap">
                <HStack spacing={2}>
                    <Text fontSize="xs" color="textSecondary" fontWeight="700" whiteSpace="nowrap">
                        Itens por página
                    </Text>
                    <SearchableSelect
                        value={pageSize}
                        onChange={value => {
                            if (value != null) onPageSizeChange(Number(value));
                        }}
                        options={pageSizeOptions}
                        placeholder="Quantidade"
                        size="sm"
                        w="82px"
                        menuPlacement="top"
                        aria-label="Itens por página"
                        bg="surface"
                        borderColor="borderPrimary"
                        color="textPrimary"
                        fontSize="xs"
                        fontWeight="800"
                        textAlign="center"
                        _focus={{ borderColor: 'secondary', boxShadow: 'none' }}
                    />
                </HStack>
                <Text fontSize="xs" color="textMuted" whiteSpace="nowrap">
                    {firstItem}–{lastItem} de {totalItems}
                </Text>
            </Flex>

            <Box maxW="100%" overflowX="auto">
                <HStack as="nav" aria-label="Paginação da lista" spacing={2} minW="max-content">
                    <Button
                        size="sm"
                        onClick={() => onPageChange(Math.max(safePage - 1, 1))}
                        isDisabled={safePage === 1}
                        variant="outline"
                        borderColor="borderPrimary"
                        color="textSecondary"
                        _hover={{ bg: 'surfaceHover', color: 'textPrimary' }}
                    >
                        Anterior
                    </Button>

                    {pageItems.map(item => (
                        typeof item === 'number' ? (
                            <Button
                                key={item}
                                size="sm"
                                minW="36px"
                                onClick={() => onPageChange(item)}
                                bg={safePage === item ? 'secondary' : 'transparent'}
                                color={safePage === item ? 'primary' : 'textSecondary'}
                                border="1px solid"
                                borderColor={safePage === item ? 'secondary' : 'borderPrimary'}
                                _hover={{
                                    bg: safePage === item ? 'secondaryHover' : 'surfaceHover',
                                    color: safePage === item ? 'primary' : 'textPrimary',
                                }}
                                fontWeight="800"
                                aria-current={safePage === item ? 'page' : undefined}
                                aria-label={`Página ${item}`}
                            >
                                {item}
                            </Button>
                        ) : (
                            <Text key={item} px={1} color="textMuted" aria-hidden="true">
                                …
                            </Text>
                        )
                    ))}

                    <Button
                        size="sm"
                        onClick={() => onPageChange(Math.min(safePage + 1, totalPages))}
                        isDisabled={safePage === totalPages}
                        variant="outline"
                        borderColor="borderPrimary"
                        color="textSecondary"
                        _hover={{ bg: 'surfaceHover', color: 'textPrimary' }}
                    >
                        Próxima
                    </Button>
                </HStack>
            </Box>
        </Flex>
    );
};

export default TablePagination;
